// src/App.js
import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Routes, Route, Link } from "react-router-dom";
import { ShoppingCart, Search } from "lucide-react";
import AutoVideo from "./components/AutoVideo";
import ForgedMonoblock from "./pages/ForgedMonoblock";
import MonoblockStyleDetail from "./pages/MonoblockStyleDetail";
import ForgedTwoPiece from "./pages/ForgedTwoPiece";
import TwoPieceStyleDetail from "./pages/TwoPieceStyleDetail";
import Warranty from "./pages/Warranty";
import ExhaustSystems from "./pages/ExhaustSystems";
import RequestExhaustQuote from "./pages/RequestExhaustQuote";
import Downpipes from "./pages/Downpipes";
import DownpipeProduct from "./pages/DownpipeProduct";
import ProductDetail from "./pages/ProductDetail";
import ExhaustProduct from "./pages/ExhaustProduct";
import CartPage from "./pages/CartPage";
import HolidayBanner from "./components/HolidayBanner";
import Policies from "./pages/Policies";
import Gallery from "./pages/Gallery";
import TruckHDWheels from "./pages/TruckHDWheels";
import TruckHDStyleDetail from "./pages/TruckHDStyleDetail";

/* ---------------- Catalog ------------------- */
const CATALOG = [
  { id: "w1", type: "wheel", brand: "Unlimited Levels", name: "Forged Monoblock", img: "/assets/monoblock.jpg" },
  { id: "w2", type: "wheel", brand: "Unlimited Levels", name: "Forged 2-piece", img: "/assets/2piece.jpg" },
  { id: "w3", type: "wheel", brand: "Unlimited Levels", name: "Forged 3-piece", img: "/assets/3piece.v2.jpg" },
  { id: "w4", type: "wheel", brand: "Unlimited Levels", name: "Carbon Fiber Wheels", img: "/assets/carbonfiber.png" },
  { id: "w6", type: "wheel", brand: "Unlimited Levels", name: "Beadlock Wheels", img: "/assets/beadlock.png" },
  { id: "w7", type: "wheel", brand: "Unlimited Levels", name: "Truck HD Wheels", img: "/assets/truckhd.jpg" }
];

/* ---------------- Wheel Presets ------------------- */
// Keys match your CATALOG names so everything stays in sync.
const WHEEL_PRESETS = {
  "Forged Monoblock": [
    "M1",
    "M2",
    "M3",
    "M4",
    "M5",
    "M6",
    "M7",
    "M8",
    "M9",
  ],
  "Forged 2-piece": [
    "T1",
    "T2",
    "T3",
    "T4",
    "T5",
    "T6",
  ],
  "Forged 3-piece": [
    "P1",
    "P2",
    "P3",
    "P4",
  ],
  "Carbon Fiber Wheels": [
    "CF1",
    "CF2",
    "CF3",
  ],
  "Beadlock Wheels": [
    "BL1",
    "BL2",
    "BL3",
  ],
  "Truck HD Wheels": [
    "HD1",
    "HD2",
    "HD3",
  ],
};

/* ========================================================================
   App (single export)
======================================================================== */
export default function App() {
  return <Shell />;
}

/* ========================================================================
   Shell: header + routes + footer
======================================================================== */
function Shell() {
  const [query, setQuery] = useState("");

  // ====================================================================
  // 1. ADDED GLOBAL CART STATE
  // ====================================================================
  // Initialize state from localStorage
  const [cartItems, setCartItems] = useState(() => {
    try {
      const localData = localStorage.getItem('unlimitedLevelsCart');
      return localData ? JSON.parse(localData) : [];
    } catch (error) {
      console.error("Could not parse cart from localStorage", error);
      return [];
    }
  });

  // Save to localStorage whenever cartItems changes
  useEffect(() => {
    localStorage.setItem('unlimitedLevelsCart', JSON.stringify(cartItems));
  }, [cartItems]);



  // ====================================================================
  // 2. ADDED CART MANAGEMENT FUNCTIONS
  // ====================================================================

  /**
   * Adds a product to the cart.
   * **IMPORTANT FOR FIX #2:** The `productToAdd` object MUST contain
   * an `imageUrl` property for the thumbnail to work.
   * e.g., { id: 'p1', name: 'Lambo Exhaust', price: 4975, imageUrl: '/path/to/img.jpg' }
   */
  const handleAddToCart = useCallback((productToAdd) => {
    if (!productToAdd.imageUrl) {
      console.warn("Product added to cart without an 'imageUrl' property. Thumbnail will be broken.");
    }

    setCartItems((prevItems) => {
      const existingItem = prevItems.find((item) => item.id === productToAdd.id);

      if (existingItem) {
        // Increase quantity
        return prevItems.map((item) =>
          item.id === productToAdd.id
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
      } else {
        // Add new item
        return [...prevItems, { ...productToAdd, quantity: 1 }];
      }
    });
  }, []); // Empty dependency array, uses functional update

  /**
   * Removes an item from the cart by its ID.
   */
  const handleRemoveFromCart = useCallback((productId) => {
    setCartItems((prevItems) => 
      prevItems.filter((item) => item.id !== productId)
    );
  }, []);

  /**
   * Updates the quantity of a specific item in the cart.
   * Removes the item if quantity drops to 0 or below.
   */
  const handleUpdateQuantity = useCallback((productId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(productId);
    } else {
      setCartItems((prevItems) =>
        prevItems.map((item) =>
          item.id === productId ? { ...item, quantity: newQuantity } : item
        )
      );
    }
  }, [handleRemoveFromCart]); // Depends on handleRemoveFromCart

  /**
   * Clears all items from the cart.
   */
  const handleClearCart = useCallback(() => {
    setCartItems([]);
  }, []);

  // ====================================================================
  // 3. ADDED LIVE ITEM COUNT CALCULATION (FOR HEADER)
  // ====================================================================
  const itemCount = useMemo(() => {
    // Sums up the 'quantity' of all items in the cart
    return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
  }, [cartItems]);


  return (
    <div className="min-h-screen bg-white text-neutral-900 font-sans">
      {/* ---------------- Header ---------------- */}
      <header className="sticky top-0 z-50 bg-white border-b border-neutral-200 shadow-sm">
        <div className="mx-auto max-w-7xl px-4">
          <div className="h-24 grid grid-cols-3 items-center">
            {/* Left: Search */}
            <div className="hidden sm:block">
              <div className="relative w-[38ch]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                <input
                  placeholder="Search wheels, exhaust, carbon…"
                  className="w-full pl-9 pr-3 py-2 rounded-xl text-sm border border-neutral-300 focus:outline-none focus:ring-2 focus:ring-neutral-900/10"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>

            {/* Center: Logo */}
            <div className="flex items-center justify-center">
              <Link to="/" aria-label="Unlimited Levels Home" className="inline-block">
                <img src="/assets/mainlogo.png?v=2" alt="Unlimited Levels" className="h-16 sm:h-20 md:h-24 object-contain" />
              </Link>
            </div>

            {/* ========================================================== */}
            {/* Right: Cart (MODIFIED FOR FIX #1) */}
            {/* ========================================================== */}
            <div className="flex justify-end">
              {/* I replaced <HeaderCartButton /> with this logic directly */}
              <Link
                to="/cart"
                className="relative inline-flex items-center p-2 text-neutral-600 hover:text-neutral-900 transition-colors"
                aria-label={`Shopping cart with ${itemCount} items`}
              >
                <ShoppingCart className="h-6 w-6" />
                
                {/* This is the live count badge */}
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-600 text-xs font-bold text-white">
                    {itemCount}
                  </span>
                )}
              </Link>
            </div>
          </div>

          {/* Main nav */}
          <nav className="flex justify-center gap-6 pb-3">
            <Link to="/wheels" className="text-sm hover:text-neutral-900">Wheels</Link>
            <Link to="/exhaust" className="text-sm hover:text-neutral-900">Exhaust</Link>
            <Link to="/gallery" className="text-sm hover:text-neutral-900">Gallery</Link>
            <Link to="/brand-ambassador" className="text-sm hover:text-neutral-900">Brand Ambassador</Link>
          </nav>
        </div>
      </header>
{/* Holiday pop-up banner */}
<HolidayBanner
  imageSrc="/assets/christmassale.png"
  href="/"
  expireDays={1}
  alt="Christmas Sale — Use code CHRISTMAS20 at checkout"
/>

            {/* ========================================================== */}
            {/* 4. MODIFIED ROUTES (TO PASS PROPS)                   */}
            {/* ========================================================== */}
<Routes>
  <Route path="/" element={<Home query={query} />} />
  <Route path="/wheels" element={<WheelsPage query={query} />} />
  <Route path="/exhaust" element={<ExhaustPage />} />
  <Route path="/brand-ambassador" element={<BrandAmbassadorPage />} />

  {/* Wheel product pages */}
  <Route path="/wheels/forged-3-piece" element={<WheelThreePiece />} />
  <Route path="/wheels/carbon-fiber" element={<WheelCarbonFiber />} />
  <Route path="/wheels/beadlock" element={<WheelBeadlock />} />
  <Route path="/build-your-custom-wheels" element={<BuildYourCustomWheels />} />
  <Route path="/wheels/forged-monoblock" element={<ForgedMonoblock />} />
  <Route path="/wheels/forged-2-piece" element={<ForgedTwoPiece />} />
  <Route path="/wheels/forged-2-piece/:slug" element={<TwoPieceStyleDetail />} />
  <Route path="/wheels/forged-two-piece/:styleSlug" element={<TwoPieceStyleDetail />} />
  <Route path="/policies" element={<Policies />} />
  <Route path="/request-exhaust-quote" element={<RequestExhaustQuote />} />
  <Route path="/gallery" element={<Gallery />} />
  <Route path="/truck-hd" element={<TruckHDWheels />} />
  <Route path="/wheels/truck-hd/:styleSlug" element={<TruckHDStyleDetail />} />

  {/* Product detail pages now get the onAddToCart function */}
  <Route 
    path="/wheels/forged-monoblock/:styleSlug" 
    element={<MonoblockStyleDetail onAddToCart={handleAddToCart} />} 
  />

  {/* Quote routes */}
  <Route path="/quote" element={<BuildYourCustomWheels />} />
  <Route path="/quote/exhaust" element={<ExhaustQuotePage />} />

  {/* Exhaust product pages */}
  <Route path="/exhaust-systems" element={<ExhaustSystems />} />
  <Route path="/downpipes" element={<Downpipes />} />
  <Route path="/downpipes/:slug" element={<DownpipeProduct onAddToCart={handleAddToCart} />} />
  <Route path="/warranty" element={<Warranty />} />

  {/* Product detail pages now get the onAddToCart function */}
  <Route 
    path="/product/:slug" 
    element={<ExhaustProduct onAddToCart={handleAddToCart} />} 
  /> 
  <Route 
    path="/exhaust/:slug" 
    element={<ExhaustProduct onAddToCart={handleAddToCart} />} 
  />
  
  {/* CartPage now gets all cart data and functions */}
  <Route 
    path="/cart" 
    element={
      <CartPage 
        cartItems={cartItems}
        onRemoveItem={handleRemoveFromCart}
        onUpdateQuantity={handleUpdateQuantity}
        onClearCart={handleClearCart}
      />
    } 
  />
  <Route path="/thanks" element={<ThankYouPage />} />
  <Route path="/thank-you" element={<ThankYouPage />} />

  {/* Fallback (keep LAST) */}
  <Route
    path="*"
    element={
      <div className="p-10">
        Page not found. <Link className="underline" to="/">Go home</Link>
      </div>
    }
  />
</Routes>

      {/* ---------------- Footer ---------------- */}
<footer className="border-t border-neutral-200 py-10 text-center text-sm text-neutral-600">
  <p>
    Follow us: @Unlimitedlevels.inc on Instagram | @UnlimitedLevels on TikTok
  </p>
  <p className="mt-2">Email: info@unlimitedlevels.co</p>

  <div className="mt-4 flex flex-wrap justify-center gap-4 text-neutral-500">
    {/* Warranty + all Stripe-compliant pages */}
    <Link to="/warranty" className="hover:text-neutral-900 transition-colors">Warranty</Link>
    <Link to="/policies#contact" className="hover:text-neutral-900 transition-colors">Contact</Link>
    <Link to="/policies#returns" className="hover:text-neutral-900 transition-colors">Returns</Link>
    <Link to="/policies#refunds" className="hover:text-neutral-900 transition-colors">Refunds</Link>
    <Link to="/policies#cancelation" className="hover:text-neutral-900 transition-colors">Cancelation</Link>
    <Link to="/policies#legal" className="hover:text-neutral-900 transition-colors">Legal/Export</Link>
    <Link to="/policies#promotions" className="hover:text-neutral-900 transition-colors">Promotions</Link>
    <Link to="/policies#privacy" className="hover:text-neutral-900 transition-colors">Privacy</Link>
    <Link to="/policies#terms" className="hover:text-neutral-900 transition-colors">Terms</Link>
  </div>

  <p className="mt-4">
    © {new Date().getFullYear()} Unlimited Levels Inc. All rights reserved.
  </p>
</footer>

     </div>
 );
}
/* ========================================================================
   Pages
======================================================================== */
function Home({ query }) {
  const products = useMemo(() => {
    return CATALOG.filter((p) => {
      const matchesCategory = p.type === "wheel";
      const matchesQuery = !query || `${p.name} ${p.brand}`.toLowerCase().includes(query.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [query]);

  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden h-[68vh] sm:h-[72vh] flex items-center justify-center">
        <video
          src="/assets/hero.mp4?v=2"
          poster="/assets/hero-fallback.png"
          autoPlay
          muted
          loop
          playsInline
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/30" />
        <a
          href="https://instagram.com/Unlimitedlevels.inc"
          target="_blank"
          rel="noreferrer"
          className="absolute right-6 bottom-6 opacity-90 hover:opacity-100 transition"
          aria-label="Instagram @Unlimitedlevels.inc"
        >
          <img
            src="/assets/instagramicon.png"
            alt="Instagram Icon"
            className="w-16 h-16 object-contain drop-shadow-lg hover:scale-105 transition-transform"
          />
        </a>
        <div className="relative z-10 text-center px-4">
          <h1 className="text-4xl sm:text-6xl font-extrabold tracking-tight text-white">Forged Without Limits.</h1>
          <p className="text-lg sm:text-2xl text-white/90 mt-2">Built to Define.</p>
          <div className="mt-6">
            <Link
              to="/gallery"
              className="px-6 py-3 bg-white text-black rounded-xl font-semibold hover:bg-neutral-200 transition inline-block"
            >
              Gallery
            </Link>
          </div>
        </div>
      </section>

      {/* Wheel Tiles */}
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
          {products.map((p) => (
            <div key={p.id} className="space-y-3 group">
              <div className="text-center">
                <div className="text-xs uppercase tracking-wide text-neutral-500">{p.brand}</div>
                <div className="text-lg font-semibold">{p.name}</div>
              </div>
              <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
                <img
                  src={p.img}
                  alt={p.name}
                  className="w-full aspect-[4/3] object-cover transition duration-300 group-hover:scale-[1.03]"
                />
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition grid place-items-center">
                  <Link
                    to={wheelProductPath(p.name)}
                    className="px-5 py-2.5 rounded-xl bg-white text-black font-semibold hover:bg-neutral-200 transition"
                  >
                    View Wheels
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Exhaust Tiles */}
      <ExhaustTiles />
    </>
  );
}

function WheelsPage({ query }) {
  const products = useMemo(() => {
    return CATALOG.filter((p) => {
      const matchesCategory = p.type === "wheel";
      const matchesQuery = !query || `${p.name} ${p.brand}`.toLowerCase().includes(query.toLowerCase());
      return matchesCategory && matchesQuery;
    });
  }, [query]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-16">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 justify-items-center">
        {products.map((p) => (
          <div key={p.id} className="space-y-3 group">
            <div className="text-center">
              <div className="text-xs uppercase tracking-wide text-neutral-500">{p.brand}</div>
              <div className="text-lg font-semibold">{p.name}</div>
            </div>
            <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
              <img
                src={p.img}
                alt={p.name}
                className="w-full aspect-[4/3] object-cover transition duration-300 group-hover:scale-[1.03]"
              />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition grid place-items-center">
                <Link
                  to={wheelProductPath(p.name)}
                  className="px-5 py-2.5 rounded-xl bg-white text-black font-semibold hover:bg-neutral-200 transition"
                >
                  View Wheels
                </Link>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ExhaustPage() {
  return <ExhaustTiles />;
}




function BuildYourCustomWheels() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Build Your Custom Wheels</h1>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Left: 9:16 vertical video */}
        <div className="rounded-2xl overflow-hidden border border-neutral-200 bg-black aspect-[9/16] md:aspect-[9/16] max-h-[80vh] mx-auto w-full">
          <video
            src="/assets/build-vertical.mp4"
            poster="/assets/build-vertical-fallback.jpg"
            autoPlay
            muted
            loop
            playsInline
            className="h-full w-full object-cover"
          />
        </div>

        {/* Right: Formspree form */}
        <form
          action="https://formspree.io/f/mgvnbvlw"
          method="POST"
          encType="multipart/form-data"
          className="space-y-4"
        >
          <input type="hidden" name="_subject" value="Wheel Quote Request — Unlimited Levels" />
          <input type="hidden" name="formName" value="Build Your Custom Wheels" />
          <input type="hidden" name="_next" value="https://unlimited-levels-d5bed.web.app/thank-you" />

          {/* Contact info */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input name="fullName" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email *</label>
              <input type="email" name="email" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input name="phone" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Location (City, State/Country)</label>
              <input name="location" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
          </div>

          {/* Car info */}
          <div className="grid sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Year *</label>
              <input name="year" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Make *</label>
              <input name="make" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Model *</label>
              <input name="model" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
            </div>
          </div>

          {/* Wheel style */}
          <div>
            <label className="block text-sm font-medium mb-1">Wheel Style *</label>
            <select name="wheelStyle" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>Forged Monoblock</option>
              <option>Forged 2-piece</option>
              <option>Forged 3-piece</option>
              <option>Carbon Fiber</option>
              <option>Beadlock</option>
              <option>Truck HD</option>
            </select>
          </div>

          {/* Wheel design / preset (optional) */}
          <div>
            <label className="block text-sm font-medium mb-1">Wheel Design / Preset</label>
            <select name="wheelPreset" className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select preset, or leave blank for fully custom</option>

              <optgroup label="Forged Monoblock (M-series)">
                <option value="M1">M1</option>
                <option value="M2">M2</option>
                <option value="M3">M3</option>
                <option value="M4">M4</option>
                <option value="M5">M5</option>
                <option value="M6">M6</option>
                <option value="M7">M7</option>
                <option value="M8">M8</option>
                <option value="M9">M9</option>
              </optgroup>

              <optgroup label="Forged 2-piece (T-series)">
                <option value="T1">T1</option>
                <option value="T2">T2</option>
                <option value="T3">T3</option>
                <option value="T4">T4</option>
                <option value="T5">T5</option>
                <option value="T6">T6</option>
              </optgroup>

              <optgroup label="Forged 3-piece (P-series)">
                <option value="P1">P1</option>
                <option value="P2">P2</option>
                <option value="P3">P3</option>
                <option value="P4">P4</option>
              </optgroup>

              <optgroup label="Carbon Fiber Wheels">
                <option value="CF1">CF1</option>
                <option value="CF2">CF2</option>
                <option value="CF3">CF3</option>
              </optgroup>

              <optgroup label="Beadlock Wheels">
                <option value="BL1">BL1</option>
                <option value="BL2">BL2</option>
                <option value="BL3">BL3</option>
              </optgroup>

              <optgroup label="Truck HD Wheels">
                <option value="TS1">TS1</option>
                <option value="TS2">TS2</option>
                <option value="TS3">TS3</option>
		<option value="TS3">TS3</option>
              </optgroup>

              <option value="Custom">Custom design (see photos / notes)</option>
            </select>
            <p className="text-xs text-neutral-500 mt-1">
              Pick a preset (M, T, P, CF, BL, HD series) or choose “Custom design” and upload your own sketch or inspiration.
            </p>
          </div>

          {/* Upload car or custom wheel design photos */}
          <div>
            <label className="block text-sm font-medium mb-1">Upload Photos of Your Car or Custom Wheel Design</label>
            <input
              type="file"
              name="photos"
              accept="image/*"
              multiple
              className="block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-black file:text-white file:px-4 file:py-2 hover:file:bg-neutral-800"
            />
            <p className="text-xs text-neutral-500 mt-1">
              Side or 3/4 angles work best. (You can attach multiple images.)
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium mb-1">Build Notes / Goals</label>
            <textarea name="notes" rows={4} className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>

          <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />
          <button
            type="submit"
            className="px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-neutral-800 transition"
          >
            Submit Request
          </button>
          <p className="text-xs text-neutral-500">
            By submitting, you agree we may contact you about your quote. We don’t share your info.
          </p>
        </form>
      </div>
    </section>
  );
}
/* ========================================================================
   Reusable & product pages
======================================================================== */
function ExhaustTiles() {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Exhaust</h2>
        <p className="text-neutral-600 mt-1">Engineered for flow, tone, and max power.</p>
      </div>

      {/* Centered 2-tile grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 justify-center max-w-3xl mx-auto">
        {/* Exhaust Systems */}
        <div className="space-y-3 group">
          <div className="text-center">
            <div className="text-lg font-semibold">Exhaust Systems</div>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
            <img
              src="/assets/exhaust-system.jpg"
              alt="Exhaust Systems"
              className="w-full aspect-[4/3] object-cover transition duration-300 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition grid place-items-center">
              <Link
                to="/exhaust-systems"
                className="px-5 py-2.5 rounded-xl bg-white text-black font-semibold hover:bg-neutral-200 transition"
              >
                View Systems
              </Link>
            </div>
          </div>
        </div>

        {/* Downpipes */}
        <div className="space-y-3 group">
          <div className="text-center">
            <div className="text-lg font-semibold">Downpipes</div>
          </div>
          <div className="relative overflow-hidden rounded-2xl border border-neutral-200 shadow-sm">
            <img
              src="/assets/downpipe.jpg"
              alt="Downpipes"
              className="w-full aspect-[4/3] object-cover transition duration-300 group-hover:scale-[1.03]"
            />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition grid place-items-center">
              <Link
                to="/downpipes"
                className="px-5 py-2.5 rounded-xl bg-white text-black font-semibold hover:bg-neutral-200 transition"
              >
                View Downpipes
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ProductPage({ title, media, children, quotePath = "/build-your-custom-wheels" }) {
  return (
    <section className="mx-auto max-w-7xl px-4 py-16">
      <h1 className="text-3xl font-bold mb-6">{title}</h1>
      <div className="grid md:grid-cols-2 gap-8">
        {media ? media : <div className="rounded-2xl border border-neutral-200 aspect-video bg-neutral-50" />}
        <div className="space-y-4">
          {children}
          <Link to={quotePath} className="inline-block px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-neutral-800 transition">
            Request a Quote
          </Link>
        </div>
      </div>
    </section>
  );
}


function WheelThreePiece() {
  return (
    <ProductPage
      title="Forged 3-Piece"
      media={<AutoVideo srcMp4="/assets/video/3piece.mp4" className="aspect-video" />}
    >
      <p className="text-neutral-700">
        The ultimate in flexibility and style—our 3-piece wheels feature fully customizable lips, barrels, and face
        finishes. Perfect for extreme fitments and show builds that demand individuality.
      </p>
    </ProductPage>
  );
}

function WheelCarbonFiber() {
  return (
    <ProductPage
      title="Carbon Fiber"
      media={<AutoVideo srcMp4="/assets/video/carbonfiber.mp4" className="aspect-video" />}
    >

      <p className="text-neutral-700">
        Ultra-lightweight construction with a pristine weave and serious performance gains. Custom finishes available.
      </p>
    </ProductPage>
  );
}

function WheelBeadlock() {
  return (
    <ProductPage title="Beadlock Wheels">
      <p className="text-neutral-700">
        Purpose-built clamping force for drag and off-road applications. Confirm local regulations for street use.
      </p>
    </ProductPage>
  );
}

function ExhaustTitanium() {
  return (
    <ProductPage
      title="Titanium Exhaust"
      quotePath="/quote/exhaust"
      media={<AutoVideo srcMp4="/assets/video/titanium.mp4" className="aspect-video" />}
    >
      <p className="text-neutral-700">Featherweight Ti construction for maximum sound character and serious weight savings.</p>
    </ProductPage>
  );
}

function ExhaustStainless() {
  return (
    <ProductPage
      title="Stainless Steel Exhaust"
      quotePath="/quote/exhaust"
      media={<AutoVideo srcMp4="/assets/video/stainless.mp4" poster="/assets/posters/stainless.jpg" className="aspect-video" />}
    >
      <p className="text-neutral-700">304/409 stainless systems engineered for longevity and an aggressive tone.</p>
    </ProductPage>
  );
}

function ExhaustDownpipes() {
  return (
    <ProductPage title="Downpipes" quotePath="/quote/exhaust">
      <p className="text-neutral-700">High-flow catted and catless options to unlock spool and top-end power.</p>
    </ProductPage>
  );
}

function ThankYouPage() {
  return (
    <section className="relative min-h-[70vh] grid place-items-center text-center">
      <img src="/assets/hero-fallback.png" alt="" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 px-6">
        <h1 className="text-white text-4xl sm:text-5xl font-extrabold tracking-tight">Thank you!</h1>
        <p className="text-white/90 mt-3 text-lg">
          Follow us on Instagram <span className="font-semibold">@UnlimitedLevels.inc</span>
        </p>
        <a
          href="https://instagram.com/Unlimitedlevels.inc"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 mt-6 px-5 py-3 bg-white text-black rounded-xl font-semibold hover:bg-neutral-200 transition"
          aria-label="Open our Instagram"
        >
          <img src="/assets/instagramicon.png" alt="Instagram" className="w-6 h-6" />
          Open Instagram
        </a>
      </div>
    </section>
  );
}

/* ========================================================================
   Helpers
======================================================================== */
function wheelProductPath(name) {
  const n = name.toLowerCase();
  if (n.includes("monoblock")) return "/wheels/forged-monoblock";
  if (n.includes("2-piece") || n.includes("2 piece")) return "/wheels/forged-2-piece";
  if (n.includes("3-piece") || n.includes("3 piece")) return "/wheels/forged-3-piece";
  if (n.includes("carbon fiber")) return "/wheels/carbon-fiber";
  if (n.includes("beadlock")) return "/wheels/beadlock";
  if (name === "Truck HD Wheels") return "/truck-hd";
  return "/wheels";
}

// Dropdown component used in GalleryPage
function Select({ value, onChange, options = [], placeholder }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-xl border border-neutral-300">
      <option value="">{placeholder}</option>
      {options.map((opt) => (
        <option key={opt} value={opt}>{opt}</option>
      ))}
    </select>
  );
}

function BrandAmbassadorPage() {
  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-4xl font-bold tracking-tight mb-2">Brand Ambassador</h1>
      <p className="text-neutral-600 mb-8">
        Join the Unlimited Levels family. Tell us about your build and audience—if accepted, we’ll reach out with the next steps.
      </p>

      <form action="https://formspree.io/f/mwpryeyb" method="POST" className="grid gap-4">
        <input type="text" name="_gotcha" className="hidden" tabIndex={-1} autoComplete="off" />
        <input type="hidden" name="_subject" value="Brand Ambassador Application — Unlimited Levels" />
        <input type="hidden" name="_next" value="https://unlimited-levels-d5bed.web.app/thank-you" />

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Full Name *</label>
            <input name="fullName" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input type="email" name="email" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Instagram Handle *</label>
            <input name="instagram" placeholder="@yourhandle" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">TikTok Handle</label>
            <input name="tiktok" placeholder="@yourtiktok" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Location (City, State, Country) *</label>
            <input name="location" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Make / Model / Year *</label>
            <input name="makeModelYear" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Current Mods</label>
          <textarea name="mods" rows={3} className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Wheel Interest</label>
            <select name="wheelInterest" className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>Forged Monoblock</option>
              <option>Forged 2-piece</option>
              <option>Forged 3-piece</option>
              <option>Carbon Fiber Wheels</option>
              <option>Magnesium Wheels</option>
              <option>Beadlock Wheels</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Audience Size</label>
            <input name="audienceSize" type="number" min="0" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Avg Views/Post</label>
            <input name="avgViews" type="number" min="0" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Links to Best Posts (comma separated)</label>
          <input name="bestLinks" placeholder="https://instagram.com/p/..., https://tiktok.com/..." className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Why do you want to represent Unlimited Levels? *</label>
          <textarea name="why" rows={4} required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
        </div>

        <div className="text-sm space-y-2">
          <label className="flex items-start gap-2">
            <input type="checkbox" name="terms" required />
            <span>I agree to be contacted about my application and I confirm all information provided is accurate. *</span>
          </label>
          <label className="flex items-start gap-2">
            <input type="checkbox" name="consent" />
            <span>I consent to Unlimited Levels featuring my submitted content and car on social media/website.</span>
          </label>
        </div>

        <div className="pt-2">
          <button type="submit" className="px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-neutral-800 transition">
            Submit Application
          </button>
        </div>
      </form>
    </section>
  );
}

function ExhaustQuotePage() {
  const FORMSPREE_ACTION = "https://formspree.io/f/xeorbgdy";
  const [exhaustType, setExhaustType] = React.useState("");

  return (
    <section className="mx-auto max-w-3xl px-4 py-12">
      <h1 className="text-3xl font-bold tracking-tight mb-6">Exhaust Quote</h1>

      <form action={FORMSPREE_ACTION} method="POST" encType="multipart/form-data" className="grid gap-4">
        <input type="hidden" name="_subject" value="Exhaust Quote — Website" />
        <input type="text" name="_gotcha" className="hidden" autoComplete="off" tabIndex={-1} />
        <input type="hidden" name="_next" value="https://unlimited-levels-d5bed.web.app/thank-you" />

        {/* Contact */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Full Name *</label>
            <input name="fullName" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Email *</label>
            <input type="email" name="email" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Phone</label>
            <input name="phone" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Location (City, State/Country)</label>
            <input name="location" className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        {/* Vehicle */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Year *</label>
            <input name="year" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Make *</label>
            <input name="make" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Model *</label>
            <input name="model" required className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
          </div>
        </div>

        {/* Product & configuration */}
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text.sm font-medium mb-1">Exhaust Type *</label>
            <select
              name="exhaustType"
              required
              className="w-full rounded-xl border border-neutral-300 px-3 py-2"
              value={exhaustType}
              onChange={(e) => setExhaustType(e.target.value)}
            >
              <option value="">Select</option>
              <option value="stainless">Stainless</option>
              <option value="titanium">Titanium</option>
              <option value="downpipes">Downpipes</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">System Type *</label>
            <select name="systemType" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>Axle-back</option>
              <option>Cat-back</option>
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Valves *</label>
            <select name="valves" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>With valves</option>
              <option>Without valves</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Mufflers *</label>
            <select name="mufflers" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>With mufflers</option>
              <option>Without mufflers</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Tip Style *</label>
          <select name="tipStyle" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
            <option value="">Select</option>
            <option>Purple Titanium</option>
            <option>Gold Titanium</option>
            <option>Blue Titanium</option>
            <option>Carbon Fiber</option>
            <option>Polished SS</option>
            <option>Gloss Black</option>
          </select>
        </div>

        {exhaustType === "titanium" && (
          <div>
            <label className="block text-sm font-medium mb-1">Titanium Color *</label>
            <select name="titaniumColor" required className="w-full rounded-xl border border-neutral-300 px-3 py-2">
              <option value="">Select</option>
              <option>Blue</option>
              <option>Gold</option>
              <option>Purple</option>
            </select>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium mb-1">Upload Photos (optional)</label>
          <input
            type="file"
            name="photos"
            multiple
            accept="image/*"
            className="block w-full text-sm file:mr-4 file:rounded-lg file:border-0 file:bg-black file:text-white file:px-4 file:py-2 hover:file:bg-neutral-800"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Notes / Goals</label>
          <textarea name="notes" rows={4} className="w-full rounded-xl border border-neutral-300 px-3 py-2" />
        </div>

        <button type="submit" className="mt-2 px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-neutral-800 transition">
          Submit Request
        </button>

        <p className="text-xs text-neutral-500">By submitting, you agree we may contact you about your quote. We don’t share your info.</p>
      </form>
    </section>
  );
}
